import React from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from 'Component/Layouts/layout';
import { Box, Typography, Card, CardContent, IconButton, Tooltip } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import NewTableComponent from 'Component/NewTable';
import useCorrectiveBranches from 'hooks/useCorrectiveBranches';
import useApiCaller from 'hooks/apiCaller';
import { toast } from 'react-toastify';

const CorrectiveBranches = () => {
  const { branches, isLoading, error } = useCorrectiveBranches();
  const navigate = useNavigate();
  const apiCaller = useApiCaller();

  const handleDownloadReport = async (e, row) => {
    e.stopPropagation(); // Prevent row click event
    if (!row.auditId) {
      toast.error('Audit ID is missing', { theme: 'colored' });
      return;
    }

    try {
      const response = await apiCaller.post(
        '/super-user/get-corrective-training-report',
        { auditId: row.auditId },
        {
          responseType: 'arraybuffer',
        }
      );

      let blobData = response.data;

      // Backend may return JSON { data, status, statusCode } instead of raw binary
      if (
        response.data &&
        typeof response.data === 'object' &&
        !(response.data instanceof ArrayBuffer) &&
        response.data.data != null
      ) {
        const data = response.data.data;
        if (typeof data === 'string') {
          try {
            // Try base64 decode first (common when backend wraps file in JSON)
            const binaryString = atob(data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            blobData = bytes.buffer;
          } catch {
            // Fallback: backend may send raw bytes as Latin-1 string (e.g. "PK\u0003\u0004...")
            const bytes = new Uint8Array(data.length);
            for (let i = 0; i < data.length; i++) {
              bytes[i] = data.charCodeAt(i) & 0xff;
            }
            blobData = bytes.buffer;
          }
        }
      }

      const blob = new Blob([blobData], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `corrective-training-report-${row.auditId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Report downloaded successfully', { theme: 'colored' });
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error(error.response?.data?.message || 'Failed to download report', {
        theme: 'colored',
      });
    }
  };

  const columns = [
    { label: 'Branch ID', field: 'auditLocationId', sortable: true },
    { label: 'Branch Name', field: 'branchName', sortable: true },
    { label: 'Review Start Date', field: 'auditReviewFromDate', sortable: true },
    { label: 'Review End Date', field: 'auditReviewToDate', sortable: true },
    { label: 'Training Status', field: 'trainingStatus', sortable: true },
    {
      label: 'Actions',
      field: 'actions',
      sortable: false,
      render: (row) => (
        <Tooltip title="Download Report">
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => handleDownloadReport(e, row)}
            sx={{
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const handleRowClick = (row) => {
    // Extract regionIds and branchIds from the row
    // Assuming the row has regionId and auditLocationId (branchId)
    const regionIds = row.regionId ? [row.regionId] : [];
    const branchIds = row.branchId ? [row.branchId] : [];
    const auditId = row.auditId;
    // Build query parameters
    const params = new URLSearchParams();
    params.append('trainingType', 'corrective-training');
    params.append('auditId', auditId);
    if (regionIds.length > 0) {
      params.append('regionIds', regionIds.join(','));
    }
    if (branchIds.length > 0) {
      params.append('branches', branchIds.join(','));
    }

    // Navigate to create-batch page with query params
    navigate(`/training/create-batch?${params.toString()}`);
  };

  return (
    <Layout>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          p: { xs: 2, md: 3 },
        }}
      >
        <Box
          sx={{
            mb: 2,
            background: '#ffffff',
            borderRadius: '8px',
            p: 2,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight="600"
                sx={{
                  color: '#1e293b',
                  mb: 0.25,
                }}
              >
                Corrective Branches
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: '400' }}>
                List of corrective branches based on audit training details.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Card
          sx={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0',
            borderRadius: '20px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            mb: 4,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {error && <Typography color="error">{error}</Typography>}
            <NewTableComponent
              tableData={branches}
              columns={columns}
              tableName="Corrective Branches"
              count={branches.length}
              loading={isLoading}
              enableSearch={true}
              enableFilters={false}
              searchPlaceholder="Search..."
              useWholeRowClicked={true}
              onRowClick={handleRowClick}
              emptyMessage="No data found"
              maxHeight="600px"
            />
          </CardContent>
        </Card>
      </Box>
    </Layout>
  );
};

export default CorrectiveBranches;
